import { Link } from 'react-router-dom'
import type { Order, TradeIdeaDetail, Transaction } from '../../api/client'
import { BelegeListe, alsBelege } from '../common/Belege'
import { Button } from '../common/Button'
import { betrag, geldtext, tag, zeitpunkt } from '../../pages/priceFormat'
import { CLASSIFICATION, RISK_PROFILE, SIDE, STATUS, stueckzahl, triggerText }
  from '../../pages/tradeIdeaVocabulary'
import { chanceRisikoAus, pct, pctText, tagKurz, tagVoll, zahl, zeitLang } from '../../hooks/ideenRegeln'
import s from './IdeeAusklapp.module.scss'

/** Ein Datum, oder ein Strich. */
function datum(wert: string | null | undefined): string {
  return wert ? tag.format(new Date(wert)) : '—'
}

/**
 * Was hinter einer Handelsidee steckt — aufgeklappt unter ihrer Zeile.
 *
 * Erst die Legende zur Leiter darüber, dann paarweise: Warum und was den
 * Einstieg begrenzt; die Stufen und die Verkaufsziele; die Position mit den Knöpfen
 * und die Orders; die Eckdaten und die Chronik. Zuletzt die Belege. Alles
 * darüber ist der Plan des Analysten, die Position ist, was daraus wurde —
 * gebucht im Depot, von Hand, nicht vom Agenten.
 *
 * Die Knöpfe erscheinen nur mit Recht und Handlung: Ohne `onOrder` gibt es
 * keinen Knopf „Order anlegen".
 */
export function IdeeAusklapp({ idee, darfSchreiben = false, darfBuchen = false,
                               onOrder, onTransaktion, onAnnehmen, onAblehnen, onAbschliessen,
                               onLoeschen, onAusfuehren, onStorno }: {
  idee: TradeIdeaDetail
  /** Darf die Idee selbst schreiben — annehmen, ablehnen, abschließen. */
  darfSchreiben?: boolean
  /** Darf im Depot buchen — Orders und Transaktionen. */
  darfBuchen?: boolean
  onOrder?: () => void
  onTransaktion?: () => void
  /** Vorgeschlagen → angenommen. Nur bei Vorschlägen. */
  onAnnehmen?: () => void
  /** → abgelehnt, mit Begründung. Bei Vorschlägen und angenommenen Ideen ohne Stücke. */
  onAblehnen?: () => void
  onAbschliessen?: () => void
  onLoeschen?: (transaktion: Transaction) => void
  onAusfuehren?: (order: Order) => void
  onStorno?: (order: Order) => void
}) {
  const lage = idee.position
  const referenz = zahl(idee.reference_price)
  const kurs = zahl(idee.last_price)
  const buchbar = darfBuchen && idee.open
  const sperre = lage.open
    ? 'Erst glatt stellen: Solange Stücke da sind, lässt sich die Idee nicht abschließen.'
    : idee.open_orders.length > 0
      ? 'Erst stornieren: Solange eine Order offen ist, lässt sich die Idee nicht abschließen.'
      : undefined
  const realisiert = Number(lage.realized?.amount ?? 0)
  const cr = chanceRisikoAus(idee, idee.targets.map((t) => t.price))

  return (
    <div className={s.ausklapp}>
      <div className={s.legende}>
        {idee.invalid_below && <span><i className={s.iTot} /> unter {geldtext(idee.invalid_below)} widerlegt</span>}
        {idee.entry_low && (
          <span><i className={s.iZone} /> Einstiegszone {idee.entry_high
            ? `${betrag(idee.entry_low.amount)} – ${geldtext(idee.entry_high)}`
            : `ab ${geldtext(idee.entry_low)}`}</span>
        )}
        {idee.chase_limit && <span><i className={s.iTeuer} /> darüber noch kaufbar, ab {geldtext(idee.chase_limit)} nicht mehr nachlaufen</span>}
        <span className={s.kurs}>
          {idee.last_price ? `Kurs ${geldtext(idee.last_price)} · ${datum(idee.last_price_at)}` : 'kein Kurs'}
          {' · '}
          <Link to={`/instruments/${idee.instrument_id}`} onClick={(e) => e.stopPropagation()}>Stammdaten</Link>
        </span>
      </div>

      <div className={s.paar}>
        <section className={s.abschnitt}>
          <h4 className={s.titel}>Warum</h4>
          {idee.rationale ? <p className={s.text}>{idee.rationale}</p>
                          : <p className={s.leer}>Keine Begründung hinterlegt.</p>}
        </section>
        <section className={s.abschnitt}>
          <h4 className={s.titel}>Was den Einstieg begrenzt</h4>
          {idee.constraint_note ? <p className={s.text}>{idee.constraint_note}</p>
                                : <p className={s.leer}>Keine Einschränkung vermerkt.</p>}
        </section>
      </div>

      <div className={s.paar}>
        <section className={s.abschnitt}>
          <h4 className={s.titel}>Der Einstieg in Stufen</h4>
          {idee.tranches.length === 0
            ? <p className={s.leer}>Keine Stufen. Die Idee wird in einem Schritt umgesetzt oder nur beobachtet.</p>
            : <div className={s.stufen}>
                {idee.tranches.map((t) => (
                  <div key={t.sequence} className={s.stufe}>
                    <span className={s.nr}>{t.sequence}</span>
                    <div>
                      <div className={s.text}>{t.note ?? '—'}</div>
                      {t.triggers.map((g, i) => (
                        <div key={i} className={s.ausloeser}>{triggerText(g, geldtext)}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>}
        </section>
        <section className={s.abschnitt}>
          <h4 className={s.titel}>Verkaufsziele</h4>
          {idee.targets.length === 0
            ? <p className={s.leer}>Kein Verkaufsziel hinterlegt.</p>
            : <div className={s.ziele}>
                <span /><span className={s.zieleKopf}>Kurs</span>
                <span className={s.zieleKopf}>zur Referenz</span><span className={s.zieleKopf}>Erreicht</span>
                {idee.targets.map((z) => {
                  const ziel = zahl(z.price)
                  const plus = referenz !== null && ziel !== null ? pct(ziel, referenz) : null
                  // „Erreicht", nicht „Stand": Der Stand ist die Idee (offen oder
                  // beendet), das Ziel hat der Kurs erreicht oder noch nicht.
                  const erreicht = z.reached_at !== null || (kurs !== null && ziel !== null && kurs >= ziel)
                  return [
                    <span key={`n${z.sequence}`} className={s.zielNr}>{z.sequence}</span>,
                    <span key={`k${z.sequence}`} className={s.zielKurs}>{geldtext(z.price)}</span>,
                    <span key={`p${z.sequence}`} className={s.zielPlus} data-minus={plus !== null && plus < 0 ? '' : undefined}>
                      {plus !== null ? pctText(plus) : '—'}
                    </span>,
                    <span key={`s${z.sequence}`} className={s.zielStand} data-erreicht={erreicht || undefined}>
                      {z.reached_at ? `ja, seit ${datum(z.reached_at)}` : erreicht ? 'ja' : 'noch nicht'}
                    </span>,
                  ]
                })}
              </div>}
        </section>
      </div>

      <div className={s.paar}>
        <section className={s.abschnitt}>
          <h4 className={s.titel}>Position</h4>
          {lage.trades === 0
            ? <p className={s.text}>
                {idee.status === 'ENTERED' ? 'Stücke im Depot.' : 'Keine Transaktion.'} Was gekauft wird,
                steht hier, gebucht im Depot, von Hand, nicht vom Agenten.
              </p>
            : <dl className={s.kennzahlen}>
                <div><dt>Stück</dt><dd>{stueckzahl(lage.quantity)}</dd></div>
                <div><dt>Ø Einstand</dt><dd>{lage.average_price ? geldtext(lage.average_price) : '—'}</dd></div>
                <div><dt>Investiert</dt><dd>{lage.invested ? geldtext(lage.invested) : '—'}</dd></div>
                <div><dt>Realisiert</dt>
                  <dd data-ton={realisiert > 0 ? 'good' : realisiert < 0 ? 'bad' : undefined}>
                    {lage.realized ? geldtext(lage.realized) : '—'}
                  </dd></div>
              </dl>}

          {idee.transactions.length > 0 && (
            <div className={s.fills}>
              <span className={s.kopf}>Wann</span><span className={s.kopf}>Seite</span>
              <span className={s.kopf}>Stück</span><span className={s.kopf}>Kurs</span>
              <span className={s.kopf}>Depot</span><span className={s.kopf}>Order</span>
              <span className={s.kopf}>Notiz</span><span className={s.kopf} />
              {idee.transactions.map((t) => [
                <span key={`w${t.id}`} className={s.fillWann}>{zeitpunkt.format(new Date(t.executed_at))}</span>,
                <span key={`s${t.id}`} className={s.fillSeite} data-seite={t.side}>{SIDE[t.side]}</span>,
                <span key={`q${t.id}`} className={s.fillZahl}>{stueckzahl(t.quantity)}</span>,
                <span key={`p${t.id}`} className={s.fillZahl}>
                  {geldtext(t.price)}
                  {Number(t.fee.amount) !== 0 && <small className={s.gebuehr}>+ {geldtext(t.fee)}</small>}
                </span>,
                <span key={`d${t.id}`} className={s.fillText} title={t.depot_name}>{t.depot_name}</span>,
                <span key={`o${t.id}`} className={s.fillZahl}>{t.order_id ? `#${t.order_id}` : '—'}</span>,
                <span key={`n${t.id}`} className={s.fillText} title={t.note ?? undefined}>{t.note ?? '—'}</span>,
                <span key={`x${t.id}`}>
                  {buchbar && onLoeschen && t.order_id === null && (
                    <button type="button" className={s.loeschen} title="Transaktion löschen"
                            aria-label="Transaktion löschen" onClick={() => onLoeschen(t)}>✕</button>
                  )}
                </span>,
              ])}
            </div>
          )}

          {idee.open && (buchbar || darfSchreiben) && (
            <div className={s.knoepfe}>
              {buchbar && onOrder && <Button variant="primary" onClick={onOrder}>Order anlegen</Button>}
              {buchbar && onTransaktion && <Button variant="plain" onClick={onTransaktion}>Transaktion eintragen</Button>}
              {darfSchreiben && onAnnehmen && idee.status === 'PROPOSED' && (
                <Button variant="plain" onClick={onAnnehmen}>Annehmen</Button>
              )}
              {darfSchreiben && onAblehnen && !lage.open
                && (idee.status === 'PROPOSED' || idee.status === 'ACTIVE') && (
                <span title={sperre}>
                  <Button variant="plain" onClick={onAblehnen} disabled={sperre !== undefined}>Ablehnen</Button>
                </span>
              )}
              {darfSchreiben && onAbschliessen && (
                <span title={sperre}>
                  <Button variant="plain" onClick={onAbschliessen} disabled={sperre !== undefined}>Abschließen</Button>
                </span>
              )}
            </div>
          )}
          {darfSchreiben && idee.open && sperre && <p className={s.hinweis}>{sperre}</p>}
        </section>

        <section className={s.abschnitt}>
          <h4 className={s.titel}>Orders</h4>
          {idee.open_orders.length === 0
            ? <p className={s.text}>Keine offene Order. Eine Order ist die Absicht zu handeln, mit Limit, Frist und Begründung.</p>
            : <div className={s.orders}>
                <span className={s.kopf}>Depot</span><span className={s.kopf}>Seite</span>
                <span className={s.kopf}>Stück</span><span className={s.kopf}>Limit</span>
                <span className={s.kopf}>Gültig bis</span><span className={s.kopf}>Begründung</span>
                <span className={s.kopf} />
                {idee.open_orders.map((o) => [
                  <span key={`d${o.id}`} className={s.fillText} title={o.depot_name}>{o.depot_name}</span>,
                  <span key={`s${o.id}`} className={s.fillSeite} data-seite={o.side}>{SIDE[o.side]}</span>,
                  <span key={`q${o.id}`} className={s.fillZahl}>{stueckzahl(o.quantity)}</span>,
                  <span key={`l${o.id}`} className={s.fillZahl}>{geldtext(o.limit_price)}</span>,
                  <span key={`v${o.id}`} className={s.fillWann}>{datum(o.valid_until)}</span>,
                  <span key={`r${o.id}`} className={s.fillText} title={o.reason}>{o.reason}</span>,
                  <span key={`k${o.id}`} className={s.orderKnoepfe}>
                    {darfBuchen && onAusfuehren && <Button variant="primary" onClick={() => onAusfuehren(o)}>Ausgeführt</Button>}
                    {darfBuchen && onStorno && <Button variant="quiet" onClick={() => onStorno(o)}>Stornieren</Button>}
                  </span>,
                ])}
              </div>}
        </section>
      </div>

      <div className={s.paar}>
        <section className={s.abschnitt}>
          <h4 className={s.titel}>Eckdaten</h4>
          <dl className={s.eckdaten}>
            {/*
              Chance und Risiko als Betraege, gerechnet vom Schnitt bis zum
              ersten Ziel und bis zur Marke — mit Umrechnung, wenn eine
              Zielwaehrung gewaehlt ist. Der Score darunter ist das
              hinterlegte Verhaeltnis des Analysten.
            */}
            <div className={s.crvBlock}>
              <div className={s.crvPaar}>
                <div>
                  <dt>Chance</dt>
                  <dd className={s.crvChance}>{cr.chance ? `+${geldtext(cr.chance)}` : '—'}</dd>
                  <span className={s.crvSub}>{cr.chanceSub}</span>
                </div>
                <div>
                  <dt>Risiko</dt>
                  <dd className={s.crvRisiko}>{cr.risiko ? `−${geldtext(cr.risiko)}` : '—'}</dd>
                  <span className={s.crvSub}>{cr.risikoSub}</span>
                </div>
              </div>
              <div className={s.crvScore}>
                <dt>Score</dt><dd>{cr.score}</dd><span className={s.crvSub}>Chance ÷ Risiko</span>
              </div>
            </div>
            <div><dt>Geplanter Schnitt</dt><dd className={s.fett}>{idee.planned_average ? geldtext(idee.planned_average) : '—'}</dd></div>
            <div><dt>Gilt bis</dt><dd>{datum(idee.valid_until)}</dd></div>
            <div><dt>Risikoprofil</dt><dd className={s.textwert}>{idee.risk_profile ? RISK_PROFILE[idee.risk_profile] ?? idee.risk_profile : '—'}</dd></div>
            <div><dt>Referenz vom</dt><dd>{datum(idee.reference_at)}</dd></div>
            {/* MIT UHRZEIT: Zwei Ideen vom selben Tag unterscheidet erst die Stunde. */}
            <div><dt>Angelegt</dt><dd>{zeitLang(idee.created)}</dd></div>
          </dl>
        </section>
        <section className={s.abschnitt}>
          <h4 className={s.titel}>Chronik</h4>
          {idee.updates.length === 0
            ? <p className={s.leer}>Noch kein Eintrag. Sobald sich etwas ändert, steht hier, was und wann, mit dem Stand danach.</p>
            : <div className={s.chronik}>
                {idee.updates.map((u) => {
                  // Das Ereignis, wenn eines genannt ist — sonst die Erfassung. Ein
                  // Ereignis trägt oft nur den Tag, die Erfassung immer die Uhrzeit.
                  const wann = u.event_at ?? u.recorded_at
                  return (
                    <div key={u.id} className={s.eintrag}>
                      <span className={s.wann} title={tagVoll(wann)}>{tagKurz(wann)}</span>
                      <div>
                        <span>{u.headline}</span>
                        {u.note && <span className={s.notiz}> · „{u.note}“</span>}
                        {u.status_after && <span className={s.delta}>Status → {STATUS[u.status_after] ?? u.status_after}</span>}
                        {u.classification_after && <span className={s.delta}>Einstufung → {CLASSIFICATION[u.classification_after] ?? u.classification_after}</span>}
                        {u.entry_low_after && <span className={s.delta}>Zone ab {geldtext(u.entry_low_after)}</span>}
                        {u.entry_high_after && <span className={s.delta}>Zone bis {geldtext(u.entry_high_after)}</span>}
                        {u.chase_limit_after && <span className={s.delta}>Obergrenze {geldtext(u.chase_limit_after)}</span>}
                        {u.invalid_below_after && <span className={s.delta}>Ungültig unter {geldtext(u.invalid_below_after)}</span>}
                      </div>
                    </div>
                  )
                })}
              </div>}
        </section>
      </div>

      <section className={s.belege}>
        <div className={s.belegeKopf}>
          <b>Belege</b>
          <span>{idee.sources.length}</span>
        </div>
        {idee.sources.length === 0
          ? <p className={s.leer}>Kein Beleg hinterlegt.</p>
          : <BelegeListe belege={alsBelege(idee.sources)} />}
      </section>
    </div>
  )
}
